import React from 'react';
import { Button, Spin } from 'antd';
import axios from 'axios';
import { store } from './redux/store';
import authAction from './redux/auth/actions';

const { logout } = authAction;

// A deploy rewrites every hashed chunk, so a tab still running the previous
// index.html asks for files that no longer exist. CloudFront answers those with
// the HTML shell (200 text/html), the dynamic import fails to parse it and lands
// here. That is recoverable by reloading once, which is what the copy asks for.
const CHUNK_ERROR = /Loading chunk [\w.-]+ failed|Loading CSS chunk|ChunkLoadError|Unexpected token '<'/i;
const RELOAD_KEY = 'chunkLoadReloadAt';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, refreshing: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);

    if (CHUNK_ERROR.test(error?.message || '')) {
      // One automatic attempt per 10s so a genuinely broken deployment cannot
      // turn into a reload loop.
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  // Check token, if no logout
  handleTokenRefresh = async () => {
    this.setState({ refreshing: true });

    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        store.dispatch(logout());
        window.location.reload();
        return;
      }

      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_API_KEY_v2 + "/Token/RefreshToken",
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          Token: accessToken,
        }),
      });

      if (response.data && response.data.RESULT) {
        localStorage.setItem("accessToken", response.data.RESULT);
        window.location.reload();
      } else {
        // No usable token: sign out and restart the app, otherwise the error
        // screen stays on display with a button that no longer does anything.
        store.dispatch(logout());
        window.location.reload();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      store.dispatch(logout());
      window.location.reload();
    } finally {
      this.setState({ refreshing: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h2>Something went wrong!</h2>
          <p>Loading failed! Please try reload the page.</p>

          {/* THE SPINNER FIX IS HERE - Lines 82-90 */}
          {this.state.refreshing ? (
            <Spin size="large" style={{ marginTop: '20px' }} />
          ) : (
            <Button
              type="primary"
              onClick={this.handleTokenRefresh}
              style={{ marginTop: '20px' }}
            >
              Refresh
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;